import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { TenantDashboardSummary } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase D0b — fetch the IA dashboard summary aggregator.
 *
 * Single source of truth for the dashboard page. Cached with a
 * deliberately SHORT staleTime (15s) because the underlying data is
 * operational (a freshly-imported student should appear quickly without
 * a hard reload). The BE sets `Cache-Control: no-store` to forbid
 * downstream HTTP caches; this hook controls in-memory React Query
 * caching, which is per-tab.
 *
 * The query is gated on the auth provider populating a tenant header;
 * the wrapper hook in `useDashboardSummary` lets callers pass `enabled:
 * false` to defer fetching during pre-auth bootstrap.
 */
export function useDashboardSummary(opts?: {
  enabled?: boolean;
}): UseQueryResult<TenantDashboardSummary> {
  return useQuery<TenantDashboardSummary>({
    queryKey: ["dashboard", "summary"],
    queryFn: async () => api.get<TenantDashboardSummary>("/me/dashboard/summary"),
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: opts?.enabled ?? true,
    refetchOnMount: "always",
  });
}
